import { Link } from '@remix-run/react';

import Typography from '~/components/Typography';
import type { Author } from '~/interfaces/author';

interface AuthorProps {
  url: string;
  alt: string;
  bgColor?: string;
}

const PostAuthor = ({ alt, url, bgColor }: AuthorProps) => {
  const fillColor = bgColor || '#ffffff';

  return (
    <div className="h-7 w-7 rounded-full">
      <img
        className="inline-block h-7 w-7 rounded-full"
        src={url}
        alt={alt}
        style={{
          border: `1px solid ${fillColor}`,
        }}
      />
    </div>
  );
};

interface Props {
  authors: Author[];
  color: string;
  font: string;
  bgColor?: string;
  fallbackName?: string;
  showAuthorImage?: boolean;
}

const PostAuthors = ({ authors, font, color, bgColor, fallbackName, showAuthorImage }: Props) => {
  // Handle cases where authors might be undefined (e.g., 403 errors on API calls)
  if (!authors || !Array.isArray(authors)) {
    return null;
  }

  const authorCount = authors.length;
  const hasMultipleAuthors = authorCount > 1;

  return (
    <Link
      to="/authors"
      prefetch="intent"
      className="group flex flex-col items-start space-x-0 space-y-1 sm:flex-row sm:items-center sm:space-x-2 sm:space-y-0"
    >
      {showAuthorImage && (
        <div className="flex -space-x-1.5 overflow-hidden">
          {authors.map((author) => {
            return <PostAuthor key={author.id} alt={author.name} url={author.profile_picture.url} bgColor={bgColor} />;
          })}
        </div>
      )}
      {authorCount > 0 ? (
        <div className="flex items-center">
          <Typography
            as="span"
            size="xs"
            weight="semibold"
            color={color}
            className="group-hover:underline"
            fontFamily={font}
            fontFamilyv2={font}
          >
            {hasMultipleAuthors ? `${authors[0].name}, +${authors.length - 1}` : authors[0].name}
          </Typography>
        </div>
      ) : (
        <Typography as="span" size="xs" weight="semibold" color={color} fontFamily={font} fontFamilyv2={font}>
          {fallbackName}
        </Typography>
      )}
    </Link>
  );
};

export default PostAuthors;
